import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { loadRuntimeBrandManifest } from "../config/runtimeBrandManifest";
import {
  getRuntimeBrandConfig,
  loadRuntimeBrandConfig,
} from "../config/runtimeBrandConfig";

/**
 * Unified brand data source for React screens.
 *
 * The single source of truth inside BrandProvider is
 * runtimeBrandConfig.js — nothing else. BrandProvider renders with an
 * instant, fully-synchronous first snapshot from getRuntimeBrandConfig()
 * (window.__BRAND_MANIFEST__ if present, else today's build-time
 * companyBrand.js / featureFlags.js / brandResolver.js values — exactly
 * as before this phase), then once, on mount, calls the async
 * loadRuntimeBrandConfig() to also try fetching
 * public/brand-manifest.generated.json. If that resolves to different
 * values, the context updates; if it fails for any reason (missing file,
 * network error, invalid JSON/shape), the instant snapshot from mount
 * stays in place. The app is never thrown into an error state: App.js
 * holds a loading screen via useBrandReady() until that fetch settles
 * (or times out), then paints the resolved brand once.
 *
 * Priority chain end to end (highest first):
 *   window.__BRAND_MANIFEST__
 *     -> public/brand-manifest.generated.json (fetched once, cached)
 *     -> build-time REACT_APP_* env values
 *     -> hardcoded ESS defaults
 *
 * BrandProvider is the ONLY place that triggers the network fetch — see
 * runtimeBrandManifest.js's in-memory cache, which also protects against
 * duplicate fetches if multiple BrandProvider instances ever mount (e.g.
 * in tests). useBrand() without a mounted provider never fetches; it only
 * ever falls back to the synchronous snapshot.
 *
 * Until the async runtime-manifest load settles, useBrandReady() is false
 * so App.js can keep a loading screen up and avoid flashing ESS defaults
 * (or any other stale snapshot) before the real brand arrives. A window
 * manifest is already the final answer, so that path is ready immediately.
 */
const BRAND_BOOT_TIMEOUT_MS = 8000;

const SAFE_FALLBACK_BRAND = {
  brandId: "ess",
  appName: "ESS HRMS",
  companyName: "ESS HRMS",
  shortName: "ESS",
  slogan: "إدارة أذكى لفريقك",
  logoPath: "/brand-logo.png",
  primaryColor: "#293681",
  secondaryColor: "#4274D9",
  featureFlags: {
    leave: true,
    payroll: true,
    attendance: true,
    overtime: true,
    evaluations: true,
    complaints: true,
    recruitment: true,
  },
};

function readBrandSnapshot() {
  try {
    const config = getRuntimeBrandConfig();
    return config && typeof config === "object"
      ? config
      : SAFE_FALLBACK_BRAND;
  } catch {
    // getRuntimeBrandConfig() already fails open internally; this is an
    // extra safety net so BrandProvider/useBrand() can never throw and
    // break the tree they're used in, no matter what changes underneath.
    return SAFE_FALLBACK_BRAND;
  }
}

function hasSynchronousBrandManifest() {
  try {
    const manifest = loadRuntimeBrandManifest();
    return Boolean(manifest && typeof manifest === "object");
  } catch {
    return false;
  }
}

// The context's default value is a brand snapshot itself, so useBrand()
// resolves to sensible values even with no <BrandProvider> mounted.
const BrandContext = createContext(null);

// Default true: without a provider there is no async fetch to wait for,
// so callers of useBrandReady() must not block on a loading screen.
const BrandReadyContext = createContext(true);

/**
 * Wraps children with the resolved brand snapshot:
 * { brandId, appName, companyName, shortName, logoPath, primaryColor,
 *   secondaryColor, featureFlags }.
 *
 * Renders a snapshot immediately (window.__BRAND_MANIFEST__ when present,
 * otherwise the current build-time / ESS values). The async runtime
 * manifest fetch still runs once on mount; useBrandReady() stays false
 * until that fetch settles (or times out), so the app shell can hold a
 * loading screen instead of painting the wrong brand first.
 */
export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(readBrandSnapshot);
  const [isReady, setIsReady] = useState(hasSynchronousBrandManifest);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) setIsReady(true);
    }, BRAND_BOOT_TIMEOUT_MS);

    loadRuntimeBrandConfig()
      .then((config) => {
        if (cancelled) return;
        if (config && typeof config === "object") {
          setBrand(config);
        }
        setIsReady(true);
      })
      .catch(() => {
        // loadRuntimeBrandConfig() already fails open internally and
        // never rejects; this catch is only an extra safety net so a
        // fetch failure can never throw into the component tree. The
        // synchronous snapshot set above simply stays in place.
        if (!cancelled) setIsReady(true);
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <BrandReadyContext.Provider value={isReady}>
      <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
    </BrandReadyContext.Provider>
  );
}

/**
 * Returns the current brand snapshot. Works even without a
 * <BrandProvider> mounted (falls back to a fresh getRuntimeBrandConfig()
 * read), so it is safe to call from any component — though no component
 * does so yet in this phase.
 */
export function useBrand() {
  const contextValue = useContext(BrandContext);
  return contextValue ?? readBrandSnapshot();
}

/**
 * False while BrandProvider is still waiting on the runtime-manifest
 * fetch. True immediately when a window manifest already answered, when
 * the fetch settles (success or fail-open), or when no provider is
 * mounted (the synchronous snapshot is all there is).
 */
export function useBrandReady() {
  return useContext(BrandReadyContext);
}

export default BrandContext;
